import { isJotformApiUrl } from 'src/utils/security/jotform-privacy';
import { isOnboardingPrivateUrl } from 'src/utils/security/onboarding-privacy';
import { isPaymentLinkApiUrl } from 'src/utils/security/redact-sensitive-data';

/**
 * First-party HTTP call observers.
 *
 * A dependency-free notification seam so more than one consumer can watch API
 * traffic. The device-local API history has its own admin toggle; diagnostic
 * logging needs the same events regardless of that toggle, and neither should
 * have to know the other exists.
 *
 * Notification is best effort: an observer that throws is isolated so it can
 * never break a request's interceptor chain.
 */

export type HttpCallStatus =
  | 'SUCCEEDED'
  | 'HTTP_ERROR'
  | 'NETWORK_ERROR'
  | 'CANCELED'
  | 'INTERRUPTED';

export type HttpCallEvent = {
  transport: 'axios' | 'fetch';
  method: string;
  url: string;
  status: HttpCallStatus;
  httpStatus: number | null;
  durationMs: number | null;
  errorCode: string | null;
  errorMessage: string | null;
  /** The caller marked this request as credential-bearing. */
  sensitive: boolean;
  /** Already scrubbed, bounded snapshots. Present only during diagnostic mode. */
  requestHeaders?: unknown;
  responseHeaders?: unknown;
  requestBody?: unknown;
  responseBody?: unknown;
  responseUrl?: string;
};

type HttpCallObserver = (event: HttpCallEvent) => void;

const observers = new Set<HttpCallObserver>();
const detailPolicies = new Map<HttpCallObserver, (url: string) => string | null>();

export function subscribeHttpCalls(
  observer: HttpCallObserver,
  detailScope?: (url: string) => string | null
): () => void {
  observers.add(observer);
  if (detailScope) detailPolicies.set(observer, detailScope);
  return () => {
    observers.delete(observer);
    detailPolicies.delete(observer);
  };
}

/** The captured scope must still match after async body reads or a pairing change. */
export function beginHttpDetailCapture(url: string): (() => boolean) | null {
  if (isOnboardingPrivateUrl(url) || isJotformApiUrl(url) || isPaymentLinkApiUrl(url)) return null;
  try {
    const policies = Array.from(detailPolicies.entries())
      .map(([observer, policy]) => ({
        observer,
        policy,
        scope: policy(url),
      }))
      .filter(({ scope }) => scope !== null);
    if (!policies.length) return null;
    return () => {
      try {
        return policies.some(
          ({ observer, policy, scope }) =>
            detailPolicies.get(observer) === policy && policy(url) === scope
        );
      } catch {
        return false;
      }
    };
  } catch {
    return null;
  }
}

export function notifyHttpCall(event: HttpCallEvent): void {
  if (isOnboardingPrivateUrl(event.url) || isJotformApiUrl(event.url) || isPaymentLinkApiUrl(event.url)) return;
  if (observers.size === 0) return;
  observers.forEach((observer) => {
    try {
      observer(event);
    } catch {
      // An observer must never affect the request it is observing.
    }
  });
}

export function clearHttpCallObserversForTests(): void {
  observers.clear();
  detailPolicies.clear();
}
